import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError, errorMessage } from "./api";

/**
 * Mutation `meta` understood by the global error handler:
 *   meta: { toast: false }            → the caller shows the error itself (forms)
 *   meta: { toast: "Could not save" } → custom toast title, API message as description
 */
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: { toast?: false | string; successToast?: string };
    queryMeta: { toastOnError?: boolean };
  }
}

/**
 * GET /subscription: the plan, licence and seats every licence screen reads
 * (the banner, Plan & billing, the members page). One key, so a refusal
 * anywhere refreshes all of them.
 */
export const SUBSCRIPTION_QUERY_KEY = ["org", "subscription"] as const;

/**
 * Refusals that mean the licence or the seat count changed under the screen:
 * a renewal lapsed, Lean2Automate paused the workspace, or somebody else took
 * the last seat. Each one refreshes the subscription, so the banner and the
 * seat line catch up with what the server just said; and each shows as ONE
 * toast however many saves meet it, because ten identical "read-only" toasts
 * after ten clicks tell nobody anything new.
 */
const LICENCE_REFUSALS: ReadonlySet<string> = new Set(["WORKSPACE_SUSPENDED", "SEAT_LIMIT_REACHED"]);

function isLicenceRefusal(error: unknown): error is ApiError {
  return error instanceof ApiError && LICENCE_REFUSALS.has(error.code);
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.code === "ABORTED") return false;
    if (error.status === 429) return failureCount < 2;
    if (error.isClientError) return false;
  }
  return failureCount < 2;
}

export function showErrorToast(err: unknown, title?: string): void {
  const message = errorMessage(err);
  const requestId = err instanceof ApiError ? err.requestId : undefined;
  toast.error(title ?? message, {
    description: title ? message : requestId ? `Reference: ${requestId}` : undefined,
    ...(isLicenceRefusal(err) ? { id: err.code } : {}),
  });
}

export function createQueryClient(): QueryClient {
  const refreshLicence = (error: unknown) => {
    if (isLicenceRefusal(error)) void client.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
  };

  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        refreshLicence(error);
        // Only surface background failures of queries that already had data (the
        // page shows its own error state on first load), or opted-in queries.
        if (query.meta?.toastOnError || (query.state.data !== undefined && !(error instanceof ApiError && error.status === 401))) {
          showErrorToast(error, "Could not refresh data");
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        // Before the opt-out: a form that shows its own error still needs the
        // banner to learn the workspace went read-only.
        refreshLicence(error);
        const meta = mutation.meta;
        if (meta?.toast === false) return;
        if (error instanceof ApiError && error.status === 401) return;
        showErrorToast(error, typeof meta?.toast === "string" ? meta.toast : undefined);
      },
      onSuccess: (_data, _vars, _ctx, mutation) => {
        if (mutation.meta?.successToast) toast.success(mutation.meta.successToast);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
  return client;
}
